import { StaffGetQueryRouteInput } from '@repo/docs-api';
import {
  StaffDeleteNoteRouteResponse,
  StaffGetNotesQueryRouteResponse,
  StaffGetQueryRouteResponse,
  StaffGetRootRouteResponse,
  StaffPostNewRouteBodyInput,
  StaffPostNewRouteResponse,
  StaffPostNoteNewRouteBodyInput,
  StaffPostNoteNewRouteResponse,
  StaffPutNoteUpdateRouteBodyInput,
  StaffPutNoteUpdateRouteResponse,
  StaffPutUpdateRouteBodyInput,
  StaffPutUpdateRouteResponse,
} from '@repo/types/api';
import { FlattenRoute } from '@repo/types/common';
import { formatStaffName } from '@repo/utils/common';
import { FieldOption, UserGroup } from '@repo/validation';

import { cognitoApi } from '../api';
import { OPTION_LIST_CACHE_SECONDS } from '../utils';

// Define a service using a base URL and expected endpoints
export const staffApi = cognitoApi.injectEndpoints({
  endpoints: build => ({
    newStaff: build.mutation<StaffPostNewRouteResponse, { attributes: StaffPostNewRouteBodyInput }>(
      {
        query(arg) {
          const { attributes } = arg;

          return {
            url: 'staff/new',
            body: attributes,
            method: 'POST',
          };
        },
        invalidatesTags: ['Users', 'Staff'],
      }
    ),
    getStaff: build.query<StaffGetRootRouteResponse, { staffId: string }>({
      query: arg => `staff/${arg.staffId ?? ''}`,
      providesTags: (_result, _error, arg) => [{ type: 'Staff', id: arg.staffId }],
    }),
    getStaffs: build.query<
      StaffGetQueryRouteResponse,
      FlattenRoute<StaffGetQueryRouteInput> & { limit?: number | 'all' }
    >({
      query: arg => {
        const { excludeDisabled, search, group, limit } = arg;
        return {
          url: 'staff/query',
          method: 'GET',
          params: {
            excludeDisabled,
            search,
            group,
            limit,
          },
        };
      },
      providesTags: result => [
        ...(result?.data.map(({ id }) => ({ type: 'Staff' as const, id })) ?? []),
        'Staff',
        { type: 'Staff', id: 'LIST' },
      ],
    }),
    // Disabled teachers included, so one still assigned to a record stays chosen
    getTeacherOptions: build.query<FieldOption[], void>({
      query: () => ({
        url: 'staff/query',
        method: 'GET',
        params: { group: UserGroup.Teachers },
      }),
      keepUnusedDataFor: OPTION_LIST_CACHE_SECONDS,
      providesTags: [{ type: 'Staff', id: 'LIST' }],
      transformResponse: (response: StaffGetQueryRouteResponse) =>
        response.data
          .map(teacher => ({ value: teacher.id, label: formatStaffName(teacher) }))
          .sort((a, b) => a.label.localeCompare(b.label)),
    }),
    updateStaff: build.mutation<
      StaffPutUpdateRouteResponse,
      { staffId: string; newAttributes: StaffPutUpdateRouteBodyInput }
    >({
      query(arg) {
        const { staffId, newAttributes } = arg;
        return {
          url: `staff/${staffId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // A change of roles can move the member into or out of a list
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Staff', id: arg?.staffId },
        { type: 'Staff', id: 'LIST' },
      ],
    }),
    newStaffNote: build.mutation<
      StaffPostNoteNewRouteResponse,
      {
        staffId: string;
        note: string;
        attributes?: Omit<StaffPostNoteNewRouteBodyInput, 'note'>;
      }
    >({
      query(arg) {
        const { staffId, note, attributes } = arg;

        return {
          url: `staff/${staffId}/notes/new`,
          body: {
            note,
            ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: ['UserNotes'],
    }),
    getStaffNotes: build.query<
      StaffGetNotesQueryRouteResponse,
      {
        staffId: string;
        active?: boolean;
        limit?: number | 'all';
      }
    >({
      query(arg) {
        const { staffId, active, limit } = arg;

        return {
          url: `staff/${staffId}/notes/query`,
          params: { active, limit },
          method: 'GET',
        };
      },
      providesTags: result =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'UserNotes' as const, id })), 'UserNotes']
          : ['UserNotes'],
    }),
    updateStaffNote: build.mutation<
      StaffPutNoteUpdateRouteResponse,
      {
        userNoteId: string;
        newAttributes: StaffPutNoteUpdateRouteBodyInput;
      }
    >({
      query(arg) {
        const { userNoteId, newAttributes } = arg;

        return {
          url: `staff/notes/${userNoteId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // Every user note should be invalidated if the due date of the note is updated, as
      // a note without a due date could gain a due date; in which case it should show
      // up in the follow up page
      invalidatesTags: (_result, _error, arg) =>
        arg.newAttributes.dueDatetime ? ['UserNotes'] : [{ type: 'UserNotes', id: arg.userNoteId }],
    }),
    deleteStaffNote: build.mutation<StaffDeleteNoteRouteResponse, { userNoteId: string }>({
      query(arg) {
        return {
          url: `staff/notes/${arg.userNoteId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_result, _error, arg) => [{ type: 'UserNotes', id: arg.userNoteId }],
    }),
  }),
});

export const {
  useLazyGetStaffQuery,
  useNewStaffMutation,
  useGetStaffQuery,
  useGetStaffsQuery,
  useGetTeacherOptionsQuery,
  useUpdateStaffMutation,
  useNewStaffNoteMutation,
  useGetStaffNotesQuery,
  useDeleteStaffNoteMutation,
  useUpdateStaffNoteMutation,
} = staffApi;
